import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { getInvoiceDetails } from '../../../../redux/actions/SocietyPortal/InvoiceAction/InvoiceAction';
import InvoiceTemplateRenderer from './components/InvoiceTemplateRenderer';
import usePrintIsolation from '../../../../utils/usePrintIsolation';

const PrintInvoice = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const { invoiceDetails, detailsLoading } = useSelector(state => state.InvoiceReducer || {});

    const printRef = useRef(null);
    // Hide the surrounding portal chrome (sidebar/header/footer/background) while printing.
    usePrintIsolation(printRef, !!invoiceDetails);

    useEffect(() => {
        if (id) {
            dispatch(getInvoiceDetails({ id }));
        }
    }, [dispatch, id]);

    const handlePrint = () => window.print();
    const handleBack = () => navigate(-1);

    if (detailsLoading || !invoiceDetails) {
        return (
            <div className="tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-bg-gray-50">
                <div className="tw-text-center">
                    <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-indigo-600 tw-mx-auto"></div>
                    <p className="tw-text-gray-500 tw-mt-4 tw-text-sm">Loading invoice...</p>
                </div>
            </div>
        );
    }

    const documentNumber = invoiceDetails.document_number || `${invoiceDetails.series?.prefix || ''}${invoiceDetails.invoice_id}`;

    return (
        <>
            {/* Print-specific styles */}
            <style>{`
                @media print {
                    body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                    /* Hide app chrome (sidebar/header/footer) and stop ancestors clipping content */
                    .print-hide { display: none !important; }
                    .print-expand { height: auto !important; min-height: 0 !important; max-height: none !important; overflow: visible !important; }
                    /* Drop the on-screen gray page background so it doesn't print */
                    .print-area { background: #fff !important; padding: 0 !important; min-height: 0 !important; }
                    .print-page { margin: 0; padding: 6mm; box-shadow: none !important; border: none !important; }
                    @page { size: A4; margin: 8mm; }
                }
            `}</style>

            {/* Action Bar - hidden in print */}
            <div className="no-print tw-bg-white tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10 tw-shadow-sm">
                <div className="tw-max-w-4xl tw-mx-auto tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-between">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button onClick={handleBack} className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500 tw-transition-colors">
                            <Icon icon="mdi:arrow-left" className="tw-text-xl" />
                        </button>
                        <div>
                            <h1 className="tw-text-lg tw-font-bold tw-text-gray-900">Print Invoice</h1>
                            <p className="tw-text-xs tw-text-gray-500">{documentNumber}</p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button
                            onClick={handleBack}
                            className="tw-px-4 tw-py-2 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 tw-transition-colors"
                        >
                            Back
                        </button>
                        <button
                            onClick={handlePrint}
                            className="tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-bg-indigo-600 tw-text-white tw-rounded-lg tw-text-sm tw-font-semibold hover:tw-bg-indigo-700 tw-transition-colors tw-shadow-sm"
                        >
                            <Icon icon="mdi:printer" className="tw-text-lg" /> Print
                        </button>
                    </div>
                </div>
            </div>

            {/* Invoice Print Layout */}
            <div ref={printRef} className="print-area tw-bg-gray-100 tw-min-h-screen tw-py-8">
                <InvoiceTemplateRenderer
                    invoice={invoiceDetails}
                    pageIdx={0}
                    totalPages={1}
                    className="print-page tw-max-w-4xl tw-mx-auto tw-bg-white tw-shadow-lg tw-border tw-border-gray-200"
                />
            </div>
        </>
    );
};

export default PrintInvoice;
